import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  format,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, addWeeks, subWeeks,
  isSameMonth, isToday, parseISO,
} from 'date-fns'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Phone, MessageSquare, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export type CalendarView = 'month' | 'week' | 'day'

export interface CalendarBooking {
  id: string
  source: string
  type: 'call' | 'chat' | 'task' | 'calendar'
  callerName: string | null
  phone: string | null
  date: string
  /** True when `date` is a backend-resolved UTC ISO (from `scheduledAt`)
      rather than a fallback to `created_at`. When true, don't re-resolve
      the relative `scheduledDate` string — doing so double-applies the
      "tomorrow" offset and lands the booking on the wrong day. */
  dateResolved?: boolean
  summary: string | null
  transcript?: string | null
  sentiment: string | null
  qualificationScore: number | null
  caseType: string | null
  duration: number | null
  conversationId?: string
  addressed?: boolean
  note?: string | null
  scheduledDate?: string | null
  scheduledTime?: string | null
  /** 'appointment' = specific clock time agreed (renders blue, time-anchored).
   *  'callback_followup' = AI promised to call within a window with no clock
   *  time (e.g. "within 24 hours"). Renders as a day-level green pill at the
   *  top of the day rather than as a misleading 12:00 AM block. */
  bookingType?: 'appointment' | 'callback_followup'
  attorneyReminderEnabled?: boolean
  clientConfirmationEnabled?: boolean
  reminderSent?: boolean
  confirmationSent?: boolean
  isExternalCalendarEvent?: boolean
  externalHtmlLink?: string
  externalCalendarProvider?: string
  externalEventId?: string
  calendarEventLocation?: string
  calendarEventDescription?: string
  nylasEventId?: string | null
}

/** Right-click target. Drives the context-menu's available actions. */
export type CalendarContextTarget =
  | { kind: 'booking'; booking: CalendarBooking }
  | { kind: 'slot'; date: Date; hour: number }
  | { kind: 'day'; date: Date }

interface BookingsCalendarProps {
  bookings: CalendarBooking[]
  view: CalendarView
  onViewChange: (view: CalendarView) => void
  onBookingClick: (booking: CalendarBooking) => void
  onAddTask?: (date: Date, hour?: number) => void
  /** Fires on right-click (desktop) or long-press (touch). Coordinates are
      viewport-relative for floating-menu positioning. */
  onContextMenu?: (target: CalendarContextTarget, x: number, y: number) => void
  /** Fires when a booking is dragged and dropped on a different day/slot.
      Parent should open a confirm dialog with `proposedStart` pre-filled —
      user can fine-tune time and confirm cascade options before the API hit. */
  onReschedule?: (booking: CalendarBooking, proposedStart: Date) => void
}

// Distance the pointer must travel (after pointerdown) before we promote
// "user might be dragging" into "user is dragging". A finger wobbles
// 5-12px during a clean tap, so a touch threshold smaller than that
// causes accidental drags on every tap. 16px is the sweet spot:
// well above tap-wobble noise, low enough that a deliberate "press
// and slide" gesture activates drag immediately on the first real
// motion. Mouse can stay tight at 5px since cursors don't wobble.
const DRAG_THRESHOLD_PX_MOUSE = 5
const DRAG_THRESHOLD_PX_TOUCH = 16

interface DragState {
  booking: CalendarBooking
  ghostX: number
  ghostY: number
  /** Key of the currently hovered drop target — `YYYY-MM-DD` for month view,
      `YYYY-MM-DD#HH` for week/day view hour cells. */
  hoveredKey: string | null
  /** Source pill rect captured at pointerdown so the ghost stays anchored
      to the original column. Y of the ghost still follows the finger. */
  sourceRect: { left: number; width: number; height: number } | null
}

// Parse scheduled time like "1:00 PM", "2pm", "around 3pm", "morning" into hours/minutes
function parseTimeString(time: string | null | undefined): { hour: number; minute: number } | null {
  if (!time || time === 'to be confirmed') return null

  // Handle vague times
  const lower = time.toLowerCase().trim()
  if (lower === 'morning') return { hour: 9, minute: 0 }
  if (lower === 'afternoon') return { hour: 14, minute: 0 }
  if (lower === 'evening') return { hour: 18, minute: 0 }
  if (lower === 'noon') return { hour: 12, minute: 0 }

  // Strip "around", "about", "approximately" etc.
  const cleaned = lower.replace(/^(around|about|approximately|roughly|at)\s+/i, '')
  const match = cleaned.match(/(\d{1,2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?|am|pm)?/i)
  if (!match) return null
  let hour = parseInt(match[1])
  const minute = match[2] ? parseInt(match[2]) : 0
  const period = match[3]?.replace(/\./g, '').toLowerCase()
  if (period === 'pm' && hour < 12) hour += 12
  if (period === 'am' && hour === 12) hour = 0
  // If no AM/PM specified and hour is 1-6, assume PM for business context.
  // Law firm calls almost always happen during business hours (1pm-6pm),
  // so "call at 2" means 2pm, not 2am. Hours 7-11 are left as-is (morning).
  if (!period && hour >= 1 && hour <= 6) hour += 12
  return { hour, minute }
}

// Parse relative date strings like "tomorrow", "Monday", "next Tuesday" into actual dates
function parseScheduledDate(dateStr: string | null | undefined, referenceDate: Date): Date | null {
  if (!dateStr) return null
  const isoMatch = dateStr.match(/^\d{4}-\d{2}-\d{2}/)
  if (isoMatch) return parseISO(isoMatch[0])

  const lower = dateStr.toLowerCase().trim()
  const ref = new Date(referenceDate)

  if (lower === 'today') return ref
  if (lower === 'tomorrow') return addDays(ref, 1)

  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
  const isNext = lower.startsWith('next ')
  const dayStr = isNext ? lower.replace('next ', '') : lower
  const dayIdx = dayNames.indexOf(dayStr)
  if (dayIdx !== -1) {
    const currentDay = ref.getDay()
    let diff = dayIdx - currentDay
    if (diff <= 0 || isNext) diff += 7
    return addDays(ref, diff)
  }

  return null
}

// Get the date the booking should appear on in the calendar.
// Exported so the dashboard's calendar peek rail resolves bookings to the
// same wall-clock time the grid renders — keeps the two surfaces in sync.
export function getBookingCalendarDate(booking: CalendarBooking): Date {
  const callDate = parseISO(booking.date)
  // Trust the backend's resolved UTC timestamp when available — parsing
  // relative strings like "tomorrow" against it double-counts the offset.
  if (booking.dateResolved) return callDate
  if (booking.scheduledDate) {
    const scheduled = parseScheduledDate(booking.scheduledDate, callDate)
    if (scheduled) {
      const time = parseTimeString(booking.scheduledTime)
      if (time) {
        scheduled.setHours(time.hour, time.minute, 0, 0)
      }
      return scheduled
    }
  }
  return callDate
}

export function getBookingDisplayTime(booking: CalendarBooking): string | null {
  const time = parseTimeString(booking.scheduledTime)
  if (time) {
    const d = new Date()
    d.setHours(time.hour, time.minute, 0, 0)
    return format(d, 'h:mm a')
  }
  return null
}

// Compact label for tight surfaces ("2:00 PM" -> "2p", "10:30 AM" -> "10:30a").
// Drops the :00 minute and trims AM/PM to a single letter — fits in ~30px of
// pill width where the full label needs ~50px.
function formatTightTime(label: string): string {
  const m = label.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/)
  if (!m) return label
  const hour = m[1]
  const min = m[2] && m[2] !== '00' ? `:${m[2]}` : ''
  const period = m[3]?.toLowerCase().startsWith('p') ? 'p' : 'a'
  return `${hour}${min}${period}`
}

function getBookingHour(booking: CalendarBooking): number | null {
  const time = parseTimeString(booking.scheduledTime)
  if (time) return time.hour + time.minute / 60
  const d = parseISO(booking.date)
  return d.getHours() + d.getMinutes() / 60
}

/**
 * Lane assignment for overlapping bookings. Pills are points (no end time)
 * so we treat two bookings as overlapping when they're within 0.5h of each
 * other. Within a group of overlapping bookings, each gets a lane index;
 * the renderer splits the column horizontally so pills sit side-by-side
 * instead of stacking on top of each other and hiding the ones underneath.
 *
 * Callback follow-ups are excluded (they have no clock time and render in
 * a separate day-level strip).
 */
function computeBookingLanes(bookings: CalendarBooking[]): Map<string, { lane: number; total: number }> {
  const eligible = bookings.filter(b => b.bookingType !== 'callback_followup')
  const withHours = eligible
    .map(b => ({ b, hour: getBookingHour(b) }))
    .filter((x): x is { b: CalendarBooking; hour: number } => x.hour !== null)
    .sort((a, b) => a.hour - b.hour)

  const groups: { b: CalendarBooking; hour: number }[][] = []
  for (const item of withHours) {
    const last = groups[groups.length - 1]
    if (last && Math.abs(item.hour - last[last.length - 1].hour) < 0.5) {
      last.push(item)
    } else {
      groups.push([item])
    }
  }

  const lanes = new Map<string, { lane: number; total: number }>()
  for (const group of groups) {
    group.forEach((item, idx) => {
      lanes.set(item.b.id, { lane: idx, total: group.length })
    })
  }
  return lanes
}

/**
 * Inline style for an overlap-aware booking pill. When alone in its slot
 * the pill keeps its existing full-width position (`undefined` returned —
 * callers fall back to className-based left/right). When sharing a slot,
 * the pill is positioned at lane/total% with a small gutter between lanes
 * so the colored borders stay visually separated.
 */
function laneStyle(info: { lane: number; total: number } | undefined): React.CSSProperties | undefined {
  if (!info || info.total <= 1) return undefined
  const widthPct = 100 / info.total
  const leftPct = info.lane * widthPct
  // 3px outer margin on both ends + 2px gutter between adjacent lanes.
  // Keeps the colored left-border visible without bleeding into the next.
  return {
    left: `calc(${leftPct}% + 3px)`,
    width: `calc(${widthPct}% - 5px)`,
    right: 'auto',
  }
}

// Color styles per type
function getPillStyles(type: string, handled: boolean, isFollowup = false) {
  // Callback follow-ups (no specific time, just "follow up by today") render
  // as green day-level pills regardless of source type. Visually distinct
  // from time-anchored appointments so the team doesn't read a
  // "within-24-hours" callback as a midnight appointment.
  if (isFollowup) {
    return handled
      ? 'bg-emerald-50 text-emerald-700/60 border-emerald-300/50 dark:bg-emerald-900/15 dark:text-emerald-400/70'
      : 'bg-emerald-100 text-emerald-800 border-emerald-500 font-medium dark:bg-emerald-900/25 dark:text-emerald-300'
  }
  if (type === 'task') {
    return handled
      ? 'bg-amber-50 text-amber-600 border-amber-300/50 dark:bg-amber-900/15 dark:text-amber-400/70'
      : 'bg-amber-100 text-amber-800 border-amber-500 font-medium dark:bg-amber-900/25 dark:text-amber-300'
  }
  if (type === 'calendar') {
    return handled
      ? 'bg-violet-50 text-violet-600/70 border-violet-300/50 dark:bg-violet-900/15 dark:text-violet-400/70'
      : 'bg-violet-100 text-violet-800 border-violet-500 font-medium dark:bg-violet-900/25 dark:text-violet-300'
  }
  if (type === 'chat') {
    return handled
      ? 'bg-violet-50 text-violet-600 border-violet-300/50 dark:bg-violet-900/15 dark:text-violet-400/70'
      : 'bg-violet-100 text-violet-800 border-violet-500 font-medium dark:bg-violet-900/25 dark:text-violet-300'
  }
  // call (default)
  return handled
    ? 'bg-brand-ice text-brand-royal/60 border-brand-royal/30 dark:bg-brand-royal/15 dark:text-brand-periwinkle/60'
    : 'bg-brand-royal/20 text-brand-indigo border-brand-royal font-medium dark:bg-brand-royal/25 dark:text-brand-periwinkle'
}

function getTypeIcon(type: string) {
  if (type === 'task') return <Plus className="h-2.5 w-2.5 shrink-0" />
  if (type === 'calendar') return <CalendarIcon className="h-2.5 w-2.5 shrink-0" />
  if (type === 'chat') return <MessageSquare className="h-2.5 w-2.5 shrink-0" />
  return <Phone className="h-2.5 w-2.5 shrink-0" />
}

function BookingPill({ booking, compact = false, tight = false, onClick, onContextMenu, onPointerDown, isDragging }: {
  booking: CalendarBooking
  compact?: boolean
  /** Tight mode: smaller padding/font on mobile, abbreviated time ("2p"
      instead of "2:00 PM"), and no caller name on phone-narrow columns.
      Used by week view where 7 columns share ~360px of phone screen. */
  tight?: boolean
  onClick: () => void
  onContextMenu?: (e: React.MouseEvent) => void
  onPointerDown?: (e: React.PointerEvent) => void
  /** True when this pill is the source of an in-flight drag — fades it out
      so the ghost stands in for it visually. */
  isDragging?: boolean
}) {
  const isHandled = booking.addressed
  const isFollowup = booking.bookingType === 'callback_followup'
  // Follow-ups have no real clock time — render "Follow up" instead of a bogus 12:00 AM.
  const fullTime = isFollowup
    ? 'Follow up'
    : (getBookingDisplayTime(booking) || format(getBookingCalendarDate(booking), 'h:mm a'))
  const tightTime = isFollowup ? 'Follow up' : formatTightTime(fullTime)
  const displayTime = fullTime

  return (
    <button
      data-rs-pill="true"
      onClick={onClick}
      onContextMenu={onContextMenu}
      onPointerDown={onPointerDown}
      // Mobile drag stack — all four properties matter on iOS Safari:
      //   touchAction: none           — without this, vertical pointermove
      //                                 is hijacked by the page scroller
      //                                 before our handler runs.
      //   WebkitTouchCallout: none    — without this, ~500ms of holding a
      //                                 touch on iOS triggers the system
      //                                 callout/lookup menu, which steals
      //                                 the gesture and our pointermove
      //                                 stream dies. This is the most
      //                                 common reason "press and hold
      //                                 doesn't work" on Safari.
      //   WebkitUserSelect: none      — backup against iOS attempting to
      //                                 start a text selection on long-
      //                                 press (Tailwind's `select-none`
      //                                 sets the unprefixed property; iOS
      //                                 still respects the prefixed one).
      //   WebkitTapHighlightColor:    — kills the gray flash on tap so the
      //     transparent                 ghost-takes-over moment isn't
      //                                 visually muddled.
      style={onPointerDown ? {
        touchAction: 'none',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      } : undefined}
      className={cn(
        // min-w-0 is load-bearing on iOS Safari: without it, the button's
        // intrinsic content min-width (icon + time + name) wins over w-full,
        // and on a tight 7-column week grid the pill bleeds past the column
        // and gets clipped by the viewport edge. With min-w-0 the truncate
        // works as advertised.
        'w-full min-w-0 max-w-full text-left rounded-md leading-tight truncate transition-colors border-l-[3px] select-none',
        tight
          // Mobile: tighter padding + smaller font so the pill fits a ~50px
          // week-view column. Desktop reverts to the standard padding.
          ? 'px-1 py-0.5 text-[10px] sm:px-2 sm:py-1 sm:text-[11px]'
          : 'px-2 py-1 text-[11px]',
        'hover:shadow-sm hover:brightness-95 cursor-pointer',
        onPointerDown && 'cursor-grab active:cursor-grabbing',
        getPillStyles(booking.type, !!isHandled, isFollowup),
        isHandled && 'opacity-50',
        isDragging && 'opacity-30'
      )}
    >
      <span className={cn('flex items-center min-w-0', tight ? 'gap-0.5 sm:gap-1' : 'gap-1')}>
        {getTypeIcon(booking.type)}
        {compact ? (
          <span className="truncate">
            <span className="font-medium">{displayTime}</span>
            {(booking.callerName || booking.summary) && (
              <span className="text-muted-foreground"> · {booking.callerName || booking.summary}</span>
            )}
          </span>
        ) : tight ? (
          <>
            {/* Mobile shows the abbreviated time ("2p") and hides the name —
                a 50px column can't readably fit both. Desktop shows the full
                "2:00 PM · Caller Name" pair. */}
            <span className="font-medium shrink-0 sm:hidden">{tightTime}</span>
            <span className="font-medium shrink-0 hidden sm:inline">{displayTime}</span>
            <span className="truncate ml-0.5 hidden sm:inline">
              {booking.callerName || booking.summary || 'Unknown'}
            </span>
          </>
        ) : (
          <>
            <span className="font-medium shrink-0">{displayTime}</span>
            <span className="truncate ml-0.5">
              {booking.callerName || booking.summary || 'Unknown'}
            </span>
          </>
        )}
      </span>
    </button>
  )
}

// ─── MONTH VIEW ──────────────────────────────────────────────
function MonthView({ currentDate, bookings, onBookingClick, onAddTask, onDayClick, onContextMenu, onPillPointerDown, draggingId, hoveredKey }: {
  currentDate: Date
  bookings: CalendarBooking[]
  onBookingClick: (b: CalendarBooking) => void
  onAddTask?: (date: Date) => void
  onDayClick?: (date: Date) => void
  onContextMenu?: (target: CalendarContextTarget, x: number, y: number) => void
  onPillPointerDown?: (booking: CalendarBooking, e: React.PointerEvent) => void
  draggingId?: string | null
  hoveredKey?: string | null
}) {
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calStart = startOfWeek(monthStart)
  const calEnd = endOfWeek(monthEnd)

  const weeks: Date[][] = []
  let day = calStart
  while (day <= calEnd) {
    const week: Date[] = []
    for (let i = 0; i < 7; i++) {
      week.push(day)
      day = addDays(day, 1)
    }
    weeks.push(week)
  }

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, CalendarBooking[]>()
    bookings.forEach(b => {
      const key = format(getBookingCalendarDate(b), 'yyyy-MM-dd')
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(b)
    })
    // Sort each day's bookings by appointment time (earliest first).
    // Items without an explicit scheduled time fall back to their booking date,
    // which keeps undated entries grouped reasonably.
    map.forEach(arr => arr.sort(
      (a, b) => getBookingCalendarDate(a).getTime() - getBookingCalendarDate(b).getTime()
    ))
    return map
  }, [bookings])

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col h-full min-h-[440px]">
      <div className="grid grid-cols-7 bg-muted/30 shrink-0">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-center text-[10px] sm:text-xs font-medium text-muted-foreground py-1.5 sm:py-2 border-b">
            <span className="sm:hidden">{d}</span>
            <span className="hidden sm:inline">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][i]}</span>
          </div>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div
          key={wi}
          className="grid grid-cols-7 flex-1 min-h-[64px] overflow-hidden"
        >
          {week.map((day, di) => {
            const key = format(day, 'yyyy-MM-dd')
            const dayBookings = bookingsByDay.get(key) || []
            const inMonth = isSameMonth(day, currentDate)
            const today = isToday(day)
            const cellKey = format(day, 'yyyy-MM-dd')
            // Drag hoveredKey carries the proposed hour in month view too
            // (e.g. "2026-05-12#14") — match by day prefix so the cell still
            // highlights regardless of the Y-derived hour zone.
            const isDropTarget = inMonth && !!hoveredKey && hoveredKey.startsWith(cellKey)
            return (
              <div
                key={di}
                data-rs-day={inMonth ? cellKey : undefined}
                onContextMenu={onContextMenu && inMonth ? (e) => {
                  e.preventDefault()
                  onContextMenu({ kind: 'day', date: day }, e.clientX, e.clientY)
                } : undefined}
                className={cn(
                  'border-b border-r p-0.5 sm:p-1 transition-colors group/cell relative',
                  !inMonth && 'bg-muted/20',
                  today && 'bg-brand-ice/50 dark:bg-brand-deep/30',
                  isDropTarget && 'ring-2 ring-inset ring-brand-royal bg-brand-royal/5',
                )}
              >
                <div className="flex items-center justify-between">
                  <div className={cn(
                    'text-[10px] sm:text-xs font-medium',
                    !inMonth && 'text-muted-foreground/40',
                    today && 'text-brand-royal font-bold',
                    !today && inMonth && 'text-foreground/70',
                  )}>
                    {today ? (
                      <span className="inline-flex items-center justify-center h-4 w-4 sm:h-5 sm:w-5 rounded-full bg-brand-royal text-white text-[8px] sm:text-[10px]">
                        {format(day, 'd')}
                      </span>
                    ) : (
                      format(day, 'd')
                    )}
                  </div>
                  {onAddTask && inMonth && (
                    <button
                      onClick={() => onAddTask(day)}
                      className="h-4 w-4 rounded flex items-center justify-center text-muted-foreground/40 hover:text-brand-royal hover:bg-brand-royal/10 opacity-0 group-hover/cell:opacity-100 transition-opacity hidden sm:flex"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {/* Mobile: show dot indicators, Desktop: show pills */}
                <div className="hidden sm:block space-y-0.5 mt-0.5">
                  {dayBookings.slice(0, 3).map(b => (
                    <BookingPill
                      key={b.id}
                      booking={b}
                      compact
                      onClick={() => onBookingClick(b)}
                      onContextMenu={onContextMenu ? (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onContextMenu({ kind: 'booking', booking: b }, e.clientX, e.clientY)
                      } : undefined}
                      onPointerDown={onPillPointerDown ? (e) => onPillPointerDown(b, e) : undefined}
                      isDragging={draggingId === b.id}
                    />
                  ))}
                  {dayBookings.length > 3 && (
                    <button
                      onClick={() => onDayClick?.(day)}
                      className="text-[10px] text-brand-royal hover:underline pl-1 cursor-pointer"
                    >
                      +{dayBookings.length - 3} more
                    </button>
                  )}
                </div>
                {/* Mobile dots */}
                {dayBookings.length > 0 && (
                  <div className="sm:hidden flex gap-0.5 mt-0.5 justify-center flex-wrap">
                    {dayBookings.length <= 3 ? (
                      dayBookings.map(b => (
                        <button
                          key={b.id}
                          onClick={() => onBookingClick(b)}
                          className={cn(
                            'h-2.5 w-2.5 rounded-full',
                            b.type === 'call' ? 'bg-brand-royal' : b.type === 'chat' ? 'bg-violet-500' : b.type === 'calendar' ? 'bg-violet-500' : 'bg-amber-500',
                            b.addressed && 'opacity-40'
                          )}
                        />
                      ))
                    ) : (
                      <button
                        onClick={() => onDayClick?.(day)}
                        className="text-[9px] font-semibold text-brand-royal leading-none"
                      >
                        {dayBookings.length}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// ─── WEEK VIEW ───────────────────────────────────────────────
// 40px per hour gets more rows on a phone screen without making the grid
// feel cramped on desktop. Was 48 — but at 18 hours that's 864px total,
// which forced the user to scroll-then-pan-back-to-toolbar on mobile.
const HOUR_HEIGHT = 40
const HOURS = Array.from({ length: 18 }, (_, i) => i + 6) // 6am - 11pm

// On mobile, week view shows 3 days centered on current day
function WeekView({ currentDate, bookings, onBookingClick, onAddTask, onContextMenu, onPillPointerDown, draggingId, hoveredKey }: {
  currentDate: Date
  bookings: CalendarBooking[]
  onBookingClick: (b: CalendarBooking) => void
  onAddTask?: (date: Date, hour: number) => void
  onContextMenu?: (target: CalendarContextTarget, x: number, y: number) => void
  onPillPointerDown?: (booking: CalendarBooking, e: React.PointerEvent) => void
  draggingId?: string | null
  hoveredKey?: string | null
}) {
  const weekStart = startOfWeek(currentDate)
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  const bookingsByDay = useMemo(() => {
    const map = new Map<string, CalendarBooking[]>()
    bookings.forEach(b => {
      const key = format(getBookingCalendarDate(b), 'yyyy-MM-dd')
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(b)
    })
    // Sort each day's bookings by appointment time (earliest first).
    // Items without an explicit scheduled time fall back to their booking date,
    // which keeps undated entries grouped reasonably.
    map.forEach(arr => arr.sort(
      (a, b) => getBookingCalendarDate(a).getTime() - getBookingCalendarDate(b).getTime()
    ))
    return map
  }, [bookings])

  return (
    <div className="border rounded-lg overflow-hidden md:flex md:flex-col md:h-full md:min-h-0">
      {/* Day-of-week header. On desktop it's shrink-0 so the hour grid below
          scrolls underneath it. On mobile the page handles scroll, so the
          header just renders inline. */}
      <div className="grid grid-cols-[28px_repeat(7,minmax(0,1fr))] sm:grid-cols-[60px_repeat(7,1fr)] bg-muted/40 border-b md:shrink-0">
        <div />
        {days.map(d => (
          <div key={d.toISOString()} className={cn(
            'text-center py-1.5 sm:py-2 border-l',
            isToday(d) && 'bg-brand-ice/50 dark:bg-brand-deep/30'
          )}>
            <div className="text-[8px] sm:text-[10px] text-muted-foreground uppercase">{format(d, 'EEE')}</div>
            <div className={cn(
              'text-xs sm:text-sm font-medium',
              isToday(d) ? 'text-brand-royal font-bold' : 'text-foreground/80'
            )}>
              {isToday(d) ? (
                <span className="inline-flex items-center justify-center h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-brand-royal text-white text-[10px] sm:text-xs">
                  {format(d, 'd')}
                </span>
              ) : format(d, 'd')}
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-[28px_repeat(7,minmax(0,1fr))] sm:grid-cols-[60px_repeat(7,1fr)] md:overflow-y-auto md:flex-1 md:min-h-0">
        <div>
          {HOURS.map(h => (
            <div key={h} style={{ height: HOUR_HEIGHT }} className="border-b pr-1 sm:pr-2 flex items-center justify-end">
              <span className="text-[8px] sm:text-[10px] text-muted-foreground leading-none">
                {format(new Date(2000, 0, 1, h), 'h a')}
              </span>
            </div>
          ))}
        </div>
        {days.map(d => {
          const key = format(d, 'yyyy-MM-dd')
          const dayBookings = bookingsByDay.get(key) || []
          const dayLanes = computeBookingLanes(dayBookings)
          // Follow-ups all sit at the top of the column, so they all share
          // one lane group — five callbacks split into five slim pills
          // rather than hiding behind the first one.
          const followups = dayBookings.filter(b => b.bookingType === 'callback_followup')
          const followupLanes = new Map<string, { lane: number; total: number }>()
          followups.forEach((b, idx) => {
            followupLanes.set(b.id, { lane: idx, total: followups.length })
          })
          return (
            <div key={key} className={cn(
              'relative border-l min-w-0 overflow-hidden',
              isToday(d) && 'bg-brand-ice/20 dark:bg-brand-deep/10'
            )}>
              {HOURS.map(h => {
                const slotKey = `${format(d, 'yyyy-MM-dd')}#${h}`
                const isDropTarget = hoveredKey === slotKey
                return (
                  <div
                    key={h}
                    style={{ height: HOUR_HEIGHT }}
                    data-rs-day={format(d, 'yyyy-MM-dd')}
                    data-rs-hour={h}
                    onContextMenu={onContextMenu ? (e) => {
                      e.preventDefault()
                      onContextMenu({ kind: 'slot', date: d, hour: h }, e.clientX, e.clientY)
                    } : undefined}
                    className={cn(
                      'border-b group/slot relative',
                      isDropTarget && 'ring-2 ring-inset ring-brand-royal bg-brand-royal/10',
                    )}
                  >
                    {onAddTask && (
                      <button
                        onClick={() => onAddTask(d, h)}
                        className="absolute inset-0 w-full h-full opacity-0 group-hover/slot:opacity-100 flex items-center justify-center transition-opacity"
                      >
                        <Plus className="h-3 w-3 text-muted-foreground/30" />
                      </button>
                    )}
                  </div>
                )
              })}
              {dayBookings.map(b => {
                // Callback follow-ups have no real clock time; render at the
                // very top of the day column instead of anchoring them at the
                // call's hour (where they'd masquerade as timed appointments).
                const isFollowup = b.bookingType === 'callback_followup'
                const hour = isFollowup ? null : getBookingHour(b)
                if (!isFollowup && hour === null) return null
                const top = isFollowup ? 2 : Math.max((hour as number) - HOURS[0], 0) * HOUR_HEIGHT + 2
                const lane = laneStyle(isFollowup ? followupLanes.get(b.id) : dayLanes.get(b.id))
                return (
                  <div
                    key={b.id}
                    className={cn(
                      'absolute z-10 min-w-0 overflow-hidden',
                      // When lane-split kicks in, the inline style takes over
                      // — the className fallback only applies for solo pills.
                      lane ? '' : 'left-0.5 right-0.5',
                    )}
                    style={{ top, ...(lane || {}) }}
                  >
                    <BookingPill
                      booking={b}
                      tight
                      onClick={() => onBookingClick(b)}
                      onContextMenu={onContextMenu ? (e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onContextMenu({ kind: 'booking', booking: b }, e.clientX, e.clientY)
                      } : undefined}
                      onPointerDown={onPillPointerDown ? (e) => onPillPointerDown(b, e) : undefined}
                      isDragging={draggingId === b.id}
                    />
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── DAY VIEW ────────────────────────────────────────────────
const DAY_HOUR_HEIGHT = 60
const DAY_HOURS = Array.from({ length: 18 }, (_, i) => i + 6) // 6am - 11pm

function DayView({ currentDate, bookings, onBookingClick, onAddTask, onContextMenu, onPillPointerDown, onDayChange, draggingId, hoveredKey }: {
  currentDate: Date
  bookings: CalendarBooking[]
  onBookingClick: (b: CalendarBooking) => void
  onAddTask?: (date: Date, hour: number) => void
  onContextMenu?: (target: CalendarContextTarget, x: number, y: number) => void
  onPillPointerDown?: (booking: CalendarBooking, e: React.PointerEvent) => void
  onDayChange?: (date: Date) => void
  draggingId?: string | null
  hoveredKey?: string | null
}) {
  const dayKey = format(currentDate, 'yyyy-MM-dd')
  // 7-day strip above the hour grid — both a quick day-jump nav and a drop
  // target for cross-day reschedules. Drop preserves the booking's original
  // time (see findDropTarget's preserveHour branch).
  const stripDays = useMemo(() => {
    const start = startOfWeek(currentDate)
    return Array.from({ length: 7 }, (_, i) => addDays(start, i))
  }, [currentDate])
  const dayBookings = useMemo(
    () => bookings.filter(b => format(getBookingCalendarDate(b), 'yyyy-MM-dd') === dayKey),
    [bookings, dayKey]
  )
  // Follow-ups have no real clock time ("AI will call back within 24 hours").
  // Render them as green day-level pills above the hour grid — the same shape
  // month/week views use — instead of misanchoring them at 12:00 AM.
  const followupBookings = useMemo(
    () => dayBookings.filter(b => b.bookingType === 'callback_followup'),
    [dayBookings]
  )
  const timedBookings = useMemo(
    () => dayBookings.filter(b => b.bookingType !== 'callback_followup'),
    [dayBookings]
  )
  const dayLanes = useMemo(() => computeBookingLanes(timedBookings), [timedBookings])

  return (
    <div className="border rounded-lg overflow-hidden md:flex md:flex-col md:h-full md:min-h-0">
      <div className={cn(
        'px-4 py-3 border-b bg-muted/30 md:shrink-0',
        isToday(currentDate) && 'bg-brand-ice/50 dark:bg-brand-deep/30'
      )}>
        <div className="flex items-center gap-2">
          {isToday(currentDate) && (
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brand-royal text-white text-sm font-bold">
              {format(currentDate, 'd')}
            </span>
          )}
          <div>
            <div className={cn(
              'text-sm font-medium',
              isToday(currentDate) ? 'text-brand-royal' : 'text-foreground'
            )}>
              {format(currentDate, 'EEEE, MMMM d, yyyy')}
            </div>
            <div className="text-xs text-muted-foreground">
              {dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>
      {/* Mini week strip — tap to jump to a day, or drop a booking onto a day
          to reschedule it (preserves the booking's original time). The strip
          is the cross-day-drag mechanism for day view, where there's no
          adjacent day in the grid itself. */}
      <div className="grid grid-cols-7 border-b bg-muted/10 md:shrink-0">
        {stripDays.map(d => {
          const stripKey = format(d, 'yyyy-MM-dd')
          const isCurrent = stripKey === dayKey
          const isStripDropTarget = hoveredKey === `strip:${stripKey}`
          return (
            <button
              key={stripKey}
              type="button"
              data-rs-strip-day={stripKey}
              onClick={() => onDayChange?.(d)}
              className={cn(
                'flex flex-col items-center gap-0.5 py-1.5 sm:py-2 border-r last:border-r-0 transition-colors',
                'text-muted-foreground hover:bg-muted/40',
                isCurrent && 'bg-brand-royal/10 text-brand-royal hover:bg-brand-royal/15',
                isStripDropTarget && 'bg-brand-royal/25 ring-2 ring-inset ring-brand-royal',
              )}
            >
              <span className="text-[8px] sm:text-[9px] uppercase font-medium leading-none">
                {format(d, 'EEE')}
              </span>
              <span className={cn(
                'text-xs sm:text-sm font-semibold leading-none',
                isToday(d) && !isCurrent && 'text-brand-royal',
              )}>
                {format(d, 'd')}
              </span>
            </button>
          )
        })}
      </div>
      {followupBookings.length > 0 && (
        <div className="border-b px-3 py-2 bg-muted/20 space-y-1 md:shrink-0">
          {followupBookings.map(b => (
            <BookingPill
              key={b.id}
              booking={b}
              compact
              onClick={() => onBookingClick(b)}
              onContextMenu={onContextMenu ? (e) => {
                e.preventDefault()
                e.stopPropagation()
                onContextMenu({ kind: 'booking', booking: b }, e.clientX, e.clientY)
              } : undefined}
              onPointerDown={onPillPointerDown ? (e) => onPillPointerDown(b, e) : undefined}
              isDragging={draggingId === b.id}
            />
          ))}
        </div>
      )}
      <div className="grid grid-cols-[50px_1fr] sm:grid-cols-[70px_1fr] md:overflow-y-auto md:flex-1 md:min-h-0">
        <div>
          {DAY_HOURS.map(h => (
            <div key={h} style={{ height: DAY_HOUR_HEIGHT }} className="border-b pr-1.5 sm:pr-3 flex items-center justify-end">
              <span className="text-[10px] sm:text-xs text-muted-foreground leading-none">
                {format(new Date(2000, 0, 1, h), 'h a')}
              </span>
            </div>
          ))}
        </div>
        <div className="relative border-l">
          {DAY_HOURS.map(h => {
            const slotKey = `${format(currentDate, 'yyyy-MM-dd')}#${h}`
            const isDropTarget = hoveredKey === slotKey
            return (
              <div
                key={h}
                style={{ height: DAY_HOUR_HEIGHT }}
                data-rs-day={format(currentDate, 'yyyy-MM-dd')}
                data-rs-hour={h}
                onContextMenu={onContextMenu ? (e) => {
                  e.preventDefault()
                  onContextMenu({ kind: 'slot', date: currentDate, hour: h }, e.clientX, e.clientY)
                } : undefined}
                className={cn(
                  'border-b group/slot relative',
                  isDropTarget && 'ring-2 ring-inset ring-brand-royal bg-brand-royal/10',
                )}
              >
                {onAddTask && (
                  <button
                    onClick={() => onAddTask(currentDate, h)}
                    className="absolute inset-0 w-full h-full opacity-0 group-hover/slot:opacity-100 flex items-center justify-center transition-opacity"
                  >
                    <span className="text-[10px] text-muted-foreground/40 flex items-center gap-0.5">
                      <Plus className="h-3 w-3" /> Add task
                    </span>
                  </button>
                )}
              </div>
            )
          })}
          {timedBookings.map(b => {
            const hour = getBookingHour(b)
            if (hour === null) return null
            const topHour = Math.max(hour - DAY_HOURS[0], 0)
            const top = topHour * DAY_HOUR_HEIGHT
            const isHandled = b.addressed
            const lane = laneStyle(dayLanes.get(b.id))
            return (
              <button
                key={b.id}
                data-rs-pill="true"
                className={cn(
                  'absolute z-10 rounded-md px-2 sm:px-3 py-1.5 text-left transition-colors border-l-[3px] select-none',
                  // Solo pills keep the existing wide layout; lane-split pills
                  // pull in inline left/width and ignore the className margins.
                  lane ? '' : 'left-0 right-0 sm:left-1 sm:right-4',
                  'hover:shadow-md cursor-pointer',
                  onPillPointerDown && 'cursor-grab active:cursor-grabbing',
                  getPillStyles(b.type, !!isHandled, false),
                  isHandled && 'opacity-50',
                  draggingId === b.id && 'opacity-30',
                )}
                // Mobile drag stack — without these, iOS Safari hijacks the
                // pointermove (touchAction), pops a callout/lookup menu after
                // ~500ms (WebkitTouchCallout), or starts a text selection
                // (WebkitUserSelect) — any of which kills press-and-hold.
                // The same set is on the BookingPill component used by month
                // and week views; this inline day-view button needs them too.
                style={onPillPointerDown ? {
                  top: top + 2,
                  touchAction: 'none',
                  WebkitTouchCallout: 'none',
                  WebkitUserSelect: 'none',
                  WebkitTapHighlightColor: 'transparent',
                  ...(lane || {}),
                } : { top: top + 2, ...(lane || {}) }}
                onClick={() => onBookingClick(b)}
                onContextMenu={onContextMenu ? (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onContextMenu({ kind: 'booking', booking: b }, e.clientX, e.clientY)
                } : undefined}
                onPointerDown={onPillPointerDown ? (e) => onPillPointerDown(b, e) : undefined}
              >
                <div className="flex items-center gap-1.5">
                  {getTypeIcon(b.type)}
                  <span className="text-xs font-medium">
                    {getBookingDisplayTime(b) || format(getBookingCalendarDate(b), 'h:mm a')}
                  </span>
                  <span className="text-xs truncate">
                    {b.callerName || b.summary || 'Unknown'}
                  </span>
                  {b.caseType && (
                    <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{b.caseType}</span>
                  )}
                </div>
                {b.summary && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{b.summary}</p>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── MAIN CALENDAR ───────────────────────────────────────────
export default function BookingsCalendar({ bookings, view, onViewChange, onBookingClick, onAddTask, onContextMenu, onReschedule }: BookingsCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date())

  // ── Drag-to-reschedule ──────────────────────────────────────
  // Distance-threshold model for both input types:
  //   Mouse:  pointerdown → 5px  motion = drag activates
  //   Touch:  pointerdown → 16px motion = drag activates (release-before
  //           crossing the threshold is a tap, opens detail panel)
  // No long-press timer. The earlier version required a 280ms hold-then-
  // slide which fought the natural "press and slide" gesture. Distance-
  // only is what iOS Notes / Snapchat / most native phone apps use.
  const [drag, setDrag] = useState<DragState | null>(null)
  // Pending state — set on pointerdown, promoted to `drag` after the
  // distance threshold for the input type is crossed.
  const pendingRef = useRef<{
    booking: CalendarBooking
    x: number
    y: number
    pointerType: string
    pointerId: number
    targetEl: Element | null
    // Source pill rect captured at pointerdown so the ghost can render
    // anchored to the same column (left + width) instead of following the
    // finger horizontally. Y still follows the finger so the booking
    // slides up/down through hour rows naturally.
    sourceRect: { left: number; width: number; height: number } | null
  } | null>(null)
  // Suppress the click-after-drop that fires when pointerup happens on the
  // original pill. Without this, dropping back onto the source slot would
  // open the booking detail panel.
  const suppressNextClickRef = useRef(false)

  const findDropTarget = useCallback((x: number, y: number): { day: string; hour: number | null; key: string; preserveHour: boolean } | null => {
    const el = document.elementFromPoint(x, y) as HTMLElement | null
    if (!el) return null
    // Day-view week strip: a horizontal row of days above the hour grid that
    // lets the user fling a booking onto a different day without leaving day
    // view. Strip cells preserve the booking's original hour — dropping an
    // 8am booking on Wednesday lands it at 8am Wednesday, not 8am-derived-
    // from-Y (which would always be the cell's tiny height = same hour).
    const strip = el.closest('[data-rs-strip-day]') as HTMLElement | null
    if (strip) {
      const day = strip.dataset.rsStripDay
      if (!day) return null
      return { day, hour: null, key: `strip:${day}`, preserveHour: true }
    }
    const cell = el.closest('[data-rs-day]') as HTMLElement | null
    if (!cell) return null
    const day = cell.dataset.rsDay
    if (!day) return null
    const hourAttr = cell.dataset.rsHour
    let hour: number
    if (hourAttr != null && hourAttr !== '') {
      // Week/Day view — hour cell has an explicit hour attribute.
      hour = Number(hourAttr)
    } else {
      // Month view — derive the hour from Y position within the day cell so
      // the user can pick a time during drag (top = morning, bottom = evening)
      // even though month cells aren't divided into hour grids visually.
      const rect = cell.getBoundingClientRect()
      const fraction = Math.max(0, Math.min(0.9999, (y - rect.top) / Math.max(rect.height, 1)))
      const MONTH_HOUR_MIN = 8   // 8 AM at top of cell
      const MONTH_HOUR_MAX = 20  // 8 PM at bottom (exclusive) — last bucket = 7 PM
      hour = MONTH_HOUR_MIN + Math.floor(fraction * (MONTH_HOUR_MAX - MONTH_HOUR_MIN))
    }
    return {
      day,
      hour,
      key: `${day}#${hour}`,
      preserveHour: false,
    }
  }, [])

  const handlePillPointerDown = useCallback((booking: CalendarBooking, e: React.PointerEvent) => {
    if (!onReschedule) return
    if (booking.isExternalCalendarEvent) return
    // Ignore right-clicks (those open the context menu).
    if (e.button !== 0) return

    // Capture the pointer on the source pill so subsequent pointermove
    // events keep firing even when the finger drifts off the pill onto a
    // calendar cell. Without this, mobile drag loses its event stream
    // the moment the finger leaves the original element.
    const targetEl = e.currentTarget as Element | null
    if (targetEl && 'setPointerCapture' in targetEl) {
      try { targetEl.setPointerCapture(e.pointerId) } catch { /* not supported / already captured */ }
    }

    // Capture the pill's geometry now so the ghost can render in the same
    // column (rather than offset to the right of the finger). getBoundingClientRect
    // is cheap and only happens on pointerdown.
    const rect = targetEl?.getBoundingClientRect()
    const sourceRect = rect
      ? { left: rect.left, width: rect.width, height: rect.height }
      : null

    pendingRef.current = {
      booking,
      x: e.clientX,
      y: e.clientY,
      pointerType: e.pointerType || 'mouse',
      pointerId: e.pointerId,
      targetEl,
      sourceRect,
    }
  }, [onReschedule])

  // Edge-paging while dragging: when the finger reaches within EDGE_PX of
  // the viewport edge during a drag, advance the calendar one period in
  // that direction (week / day / month depending on view). Lets the user
  // drop a booking onto a day that isn't currently in view without having
  // to release, navigate, and re-pick up the booking.
  const EDGE_PX = 56
  const EDGE_PAGE_COOLDOWN_MS = 650
  const edgePageRef = useRef<number>(0)

  // Document-level listeners while a drag candidate or active drag exists.
  useEffect(() => {
    if (!onReschedule) return

    const onMove = (e: PointerEvent) => {
      if (drag) {
        const target = findDropTarget(e.clientX, e.clientY)
        setDrag(prev => prev ? {
          ...prev,
          ghostX: e.clientX,
          ghostY: e.clientY,
          hoveredKey: target?.key ?? null,
        } : null)

        // Edge-paging — fire navigate() once per cooldown when the finger
        // sits inside the edge band. The drag continues; the next
        // pointermove re-evaluates findDropTarget against the newly-
        // rendered calendar so the highlight follows the finger naturally.
        const now = Date.now()
        if (now - edgePageRef.current > EDGE_PAGE_COOLDOWN_MS) {
          const w = typeof window !== 'undefined' ? window.innerWidth : 0
          let dir: -1 | 1 | 0 = 0
          if (e.clientX < EDGE_PX) dir = -1
          else if (e.clientX > w - EDGE_PX) dir = 1
          if (dir !== 0) {
            edgePageRef.current = now
            // Small haptic so the user feels the page turn under their
            // finger — without this, the calendar silently changing
            // underneath an active drag is disorienting.
            if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
              try { navigator.vibrate(8) } catch { /* unsupported */ }
            }
            if (view === 'month') setCurrentDate(d => dir === 1 ? addMonths(d, 1) : subMonths(d, 1))
            else if (view === 'week') setCurrentDate(d => dir === 1 ? addWeeks(d, 1) : subWeeks(d, 1))
            else setCurrentDate(d => addDays(d, dir))
          }
        }
      } else if (pendingRef.current) {
        const dx = e.clientX - pendingRef.current.x
        const dy = e.clientY - pendingRef.current.y
        const isTouch = pendingRef.current.pointerType === 'touch'
        const threshold = isTouch ? DRAG_THRESHOLD_PX_TOUCH : DRAG_THRESHOLD_PX_MOUSE
        if (Math.abs(dx) > threshold || Math.abs(dy) > threshold) {
          // Promote pending to active drag. On touch, fire a light haptic
          // so the user can FEEL the drag arming — premium-feel parity
          // with iOS Calendar / Apple Notes drag gestures.
          if (isTouch && typeof navigator !== 'undefined' && 'vibrate' in navigator) {
            try { navigator.vibrate(10) } catch { /* unsupported */ }
          }
          const target = findDropTarget(e.clientX, e.clientY)
          setDrag({
            booking: pendingRef.current.booking,
            ghostX: e.clientX,
            ghostY: e.clientY,
            hoveredKey: target?.key ?? null,
            sourceRect: pendingRef.current.sourceRect,
          })
        }
      }
    }

    const onUp = (e: PointerEvent) => {
      const wasDragging = !!drag
      if (wasDragging && drag) {
        const target = findDropTarget(e.clientX, e.clientY)
        if (target) {
          // Hour-cell drops (week/day view) snap to hour:00. Day-cell drops
          // (month view) derive their hour from the Y position within the
          // cell so the user can pick a time during drag — see findDropTarget.
          // Strip drops (day-view week strip) preserve the booking's original
          // hour and minute so dragging an 8:30am booking to Wednesday keeps
          // it at 8:30am Wednesday.
          const [y, m, d] = target.day.split('-').map(Number)
          const original = getBookingCalendarDate(drag.booking)
          const newStart = target.preserveHour
            ? new Date(y, m - 1, d, original.getHours(), original.getMinutes(), 0, 0)
            : new Date(y, m - 1, d, target.hour ?? 0, 0, 0, 0)
          // Skip if the user dropped back on the same slot (no-op).
          const sameSlot = newStart.getTime() === original.getTime()
          if (!sameSlot) {
            onReschedule(drag.booking, newStart)
          }
        }
        // Suppress the click that follows pointerup on the source pill.
        suppressNextClickRef.current = true
        window.setTimeout(() => { suppressNextClickRef.current = false }, 0)
      }

      // Release pointer capture on whatever element grabbed it on down,
      // so the next pointerdown gets a fresh capture cycle.
      const pending = pendingRef.current
      if (pending?.targetEl && 'releasePointerCapture' in pending.targetEl) {
        try { (pending.targetEl as Element).releasePointerCapture(pending.pointerId) } catch { /* fine */ }
      }

      pendingRef.current = null
      if (drag) setDrag(null)
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        pendingRef.current = null
        setDrag(null)
      }
    }

    // pointercancel fires when iOS interrupts our gesture (system gesture,
    // page scroll, etc.). Treat it like a clean cancel — drop pending state
    // so the next pointerdown starts fresh.
    const onCancel = () => {
      pendingRef.current = null
      setDrag(null)
    }

    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onCancel)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onCancel)
      document.removeEventListener('keydown', onKey)
    }
  }, [drag, onReschedule, findDropTarget, view])

  // Wrap the pill click handler so a drag drop doesn't also trigger detail open.
  const handleBookingClick = useCallback((b: CalendarBooking) => {
    if (suppressNextClickRef.current) return
    onBookingClick(b)
  }, [onBookingClick])

  const navigate = (dir: -1 | 1) => {
    if (view === 'month') setCurrentDate(d => dir === 1 ? addMonths(d, 1) : subMonths(d, 1))
    else if (view === 'week') setCurrentDate(d => dir === 1 ? addWeeks(d, 1) : subWeeks(d, 1))
    else setCurrentDate(d => addDays(d, dir))
  }

  // Horizontal swipe → advance to next/previous period (week, day, or month
  // depending on view). Mobile-only behavior: a deliberate left/right swipe
  // started on empty calendar space (not on a booking pill — those have
  // their own drag-to-reschedule gesture) navigates one period in that
  // direction. Vertical motion is ignored so the page can still scroll.
  const swipeRef = useRef<{ x: number; y: number; t: number } | null>(null)
  const handleSwipePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return
    const target = e.target as HTMLElement
    if (target.closest('[data-rs-pill]')) {
      // Pill has its own gesture; don't compete.
      swipeRef.current = null
      return
    }
    swipeRef.current = { x: e.clientX, y: e.clientY, t: Date.now() }
  }, [])
  const handleSwipePointerUp = useCallback((e: React.PointerEvent) => {
    const s = swipeRef.current
    swipeRef.current = null
    if (!s) return
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    const dt = Date.now() - s.t
    // Looser thresholds than the original 60px / 1.5x / 800ms: 40px is far
    // enough that a fingertip's natural wobble during a tap won't trigger,
    // but close enough that a deliberate flick reliably pages. 1.2x
    // dominance still rejects diagonals leaning vertical (which the user
    // probably meant as scroll). 1500ms allows a slower, more thoughtful
    // swipe — a finger just sliding across the screen at conversational
    // pace was getting rejected at 800ms.
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2 && dt < 1500) {
      // Light haptic so the user feels the page turn — confirms the
      // gesture registered without needing to look for the date label
      // changing.
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        try { navigator.vibrate(8) } catch { /* unsupported */ }
      }
      navigate(dx < 0 ? 1 : -1)
    }
  }, [view])

  const goToday = () => setCurrentDate(new Date())

  const goToDay = (date: Date) => {
    setCurrentDate(date)
    onViewChange('day')
  }

  const headerLabel = view === 'month'
    ? format(currentDate, 'MMMM yyyy')
    : view === 'week'
      ? `${format(startOfWeek(currentDate), 'MMM d')} – ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`
      : format(currentDate, 'MMMM d, yyyy')

  return (
    <div className="flex flex-col h-full min-h-0 gap-3">
      {/* Toolbar — sticky on mobile so the date / view-switcher stays visible
          when the page scrolls. The calendar's body has its own scroll on
          desktop, but on mobile the page itself scrolls and without sticky
          the toolbar disappears off the top. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 shrink-0 sticky top-0 z-20 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 -mx-1 px-1 py-2 sm:relative sm:bg-transparent sm:backdrop-blur-0 sm:py-0 sm:mx-0 sm:px-0">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={goToday}>
            Today
          </Button>
          <div className="flex items-center">
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <h2 className="text-sm font-semibold truncate">{headerLabel}</h2>
        </div>
        <div className="flex items-center gap-2">
          {onAddTask && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => onAddTask(currentDate)}
            >
              <Plus className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Add Task</span>
            </Button>
          )}
          <div className="flex items-center bg-muted rounded-md p-0.5">
            {(['month', 'week', 'day'] as CalendarView[]).map(v => (
              <Button
                key={v}
                variant="ghost"
                size="sm"
                className={cn(
                  'h-6 text-xs px-2 sm:px-2.5 capitalize rounded-sm',
                  view === v && 'bg-background shadow-sm text-foreground',
                  view !== v && 'text-muted-foreground hover:text-foreground'
                )}
                onClick={() => onViewChange(v)}
              >
                {v}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile-only nudge: 7 columns sharing ~360px of phone screen makes
          week-view pills cramped no matter what we do. Day view fits the
          medium fine — surface that as a one-tap hint instead of leaving
          the user to figure it out. Hidden on sm+ where week view is
          legible. */}
      {view === 'week' && (
        <button
          type="button"
          onClick={() => onViewChange('day')}
          className="sm:hidden shrink-0 -mt-1 flex items-center justify-center gap-1.5 rounded-md border border-border/60 bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground hover:bg-muted/50 active:bg-muted/60 transition-colors"
        >
          <span>Tip — Day view fits better on phones.</span>
          <span className="font-semibold text-brand-royal">Switch →</span>
        </button>
      )}

      {/* Calendar body — fills remaining height so the month grid never
          overflows. Pointer handlers here implement horizontal swipe
          navigation; they bail out if the gesture starts on a booking pill
          (data-rs-pill) so drag-to-reschedule still works. */}
      <div
        className="flex-1 min-h-0"
        onPointerDown={handleSwipePointerDown}
        onPointerUp={handleSwipePointerUp}
        onPointerCancel={() => { swipeRef.current = null }}
      >
        {view === 'month' && (
          <MonthView
            currentDate={currentDate}
            bookings={bookings}
            onBookingClick={handleBookingClick}
            onAddTask={onAddTask ? (d) => onAddTask(d) : undefined}
            onDayClick={goToDay}
            onContextMenu={onContextMenu}
            onPillPointerDown={onReschedule ? handlePillPointerDown : undefined}
            draggingId={drag?.booking.id ?? null}
            hoveredKey={drag?.hoveredKey ?? null}
          />
        )}
        {view === 'week' && (
          <WeekView
            currentDate={currentDate}
            bookings={bookings}
            onBookingClick={handleBookingClick}
            onAddTask={onAddTask}
            onContextMenu={onContextMenu}
            onPillPointerDown={onReschedule ? handlePillPointerDown : undefined}
            draggingId={drag?.booking.id ?? null}
            hoveredKey={drag?.hoveredKey ?? null}
          />
        )}
        {view === 'day' && (
          <DayView
            currentDate={currentDate}
            bookings={bookings}
            onBookingClick={handleBookingClick}
            onAddTask={onAddTask}
            onContextMenu={onContextMenu}
            onPillPointerDown={onReschedule ? handlePillPointerDown : undefined}
            onDayChange={setCurrentDate}
            draggingId={drag?.booking.id ?? null}
            hoveredKey={drag?.hoveredKey ?? null}
          />
        )}
      </div>

      {/* Drag ghost — portal-rendered so it can overflow any cell container.
          Time updates live as you hover: hour-slot drops show the hour, day-cell
          drops preserve the booking's original time. */}
      {drag && <DragGhost drag={drag} />}

      {/* Legend pinned below the flex-1 calendar body — hidden on mobile
          where every screen pixel matters and the colored dots are already
          self-explanatory in context. */}
      <div className="hidden sm:flex items-center gap-4 text-[11px] text-muted-foreground shrink-0">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-brand-royal/20 border-l-[3px] border-brand-royal" />
          Calls
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-violet-100 border-l-[3px] border-violet-500 dark:bg-violet-900/25" />
          Chats
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-amber-100 border-l-[3px] border-amber-500 dark:bg-amber-900/25" />
          Tasks
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-emerald-100 border-l-[3px] border-emerald-500 dark:bg-emerald-900/25" />
          Follow up today (no time)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-muted/50 border-l-[3px] border-muted-foreground/30 opacity-50" />
          Handled
        </span>
      </div>
    </div>
  )
}

// ─── DRAG GHOST ──────────────────────────────────────────────
// A translucent copy of the booking pill that the user is dragging.
// Anchored to the source pill's column (left + width captured on
// pointerdown) and follows the finger vertically — so the booking
// "slides" up and down through hour rows in place rather than peeling
// off as a floating card. Reads as physically picking up and moving
// the booking.
function DragGhost({ drag }: { drag: DragState }) {
  const original = getBookingCalendarDate(drag.booking)
  const isHandled = drag.booking.addressed
  const isFollowup = drag.booking.bookingType === 'callback_followup'

  // Target time label — what the user is about to drop on. Empty when
  // no cell is hovered (mid-air mid-drag); falls back to original time.
  // Strip drops (`strip:YYYY-MM-DD`) preserve the original time on a new day,
  // so show "Wed 8:00 AM" rather than just a clock time — the day change is
  // the user-visible side effect.
  const targetLabel: string = (() => {
    const k = drag.hoveredKey
    if (!k) return format(original, 'h:mm a')
    if (k.startsWith('strip:')) {
      const day = k.slice('strip:'.length)
      const [y, m, d] = day.split('-').map(Number)
      const target = new Date(y, m - 1, d, original.getHours(), original.getMinutes())
      return format(target, 'EEE h:mm a')
    }
    const [day, hourStr] = k.split('#')
    const [y, m, d] = day.split('-').map(Number)
    const hour = Number(hourStr)
    return format(new Date(y, m - 1, d, hour, 0), 'h:mm a')
  })()

  // If we somehow missed capturing the source rect (e.g. setPointerCapture
  // failed), fall back to a small floating tag near the cursor so the
  // user still sees something. Should be vanishingly rare in practice.
  if (!drag.sourceRect) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          left: drag.ghostX + 12,
          top: drag.ghostY + 12,
          pointerEvents: 'none',
          zIndex: 80,
        }}
        className="rounded-md bg-card/95 border border-border shadow-xl px-2 py-1 text-[11px] font-medium"
      >
        Move to {targetLabel}
      </div>,
      document.body
    )
  }

  // Anchored ghost: same X as the source pill, Y follows the finger
  // (centered under it so the pill feels picked up by where the finger is).
  const ghostHeight = drag.sourceRect.height
  const top = drag.ghostY - ghostHeight / 2

  return createPortal(
    <div
      // pointerEvents:none keeps the ghost from intercepting elementFromPoint.
      style={{
        position: 'fixed',
        left: drag.sourceRect.left,
        width: drag.sourceRect.width,
        top,
        pointerEvents: 'none',
        zIndex: 80,
        opacity: 0.85,
      }}
    >
      <div
        className={cn(
          'w-full text-left rounded-md px-2 py-1 text-[11px] leading-tight border-l-[3px] select-none shadow-2xl ring-1 ring-brand-royal/40',
          getPillStyles(drag.booking.type, !!isHandled, isFollowup)
        )}
      >
        <span className="flex items-center gap-1">
          {getTypeIcon(drag.booking.type)}
          <span className="font-medium shrink-0">{targetLabel}</span>
          <span className="truncate ml-0.5">
            {drag.booking.callerName || drag.booking.summary || 'Booking'}
          </span>
        </span>
      </div>
    </div>,
    document.body
  )
}
